import React from 'react';
import Helmet from 'react-helmet';
import { graphql } from 'gatsby';
import { Link } from 'gatsby';
import { MdKeyboardArrowDown } from 'react-icons/md';
import TemplateLayout, {
  Template as TemplateCard,
  TemplateGrid,
} from '../layouts/templates';
import * as styles from './template.module.styl';
import Button from '../components/Button';
import { AB, ABVariant } from '../components/AB';
import formScreenshot from '../utils/formScreenshot';

function replaceButtonBelowText(htmlString) {
  return htmlString.replace('button below', 'button');
}

function replaceUseTemplateText(htmlString) {
  const regex =
    /((<strong>((\\)?")?Use this template((\\)?")?<\/strong>)|(<em>((\\)?")?Use this template((\\)?")?<\/em>)|((\\)?"Use this template(\\)?"))(\sbelow)?/g;

  return htmlString.replace(regex, '<strong>Try without signing up</strong>');
}

class Template extends React.Component {
  constructor() {
    super();
    this.state = {};
  }

  componentDidMount() {
    if (window.top !== window) {
      this.setState({ embedded: true, loaded: true });
    } else {
      this.setState({ loaded: true });
    }
    if (!global.Paperform) {
      const script = document.createElement('script');
      script.src = 'https://paperform.co/__embed.min.js';
      document.body.appendChild(script);
    }
  }

  renderButton(variant = false) {
    if (variant) {
      return (
        <Button
          solid
          secondary
          sm
          href={`/try/templates/${this.props.template.key}/?utm_medium=growth_test&utm_source=Templates&utm_campaign=GT1&utm_content=${this.props.template.key}`}
          style={{ margin: '.5rem', marginLeft: 0 }}
        >
          Try without signing up
        </Button>
      );
    }
    return (
      <Button
        solid
        secondary
        sm
        href={`/create?template=${this.props.template.key}`}
        style={{ margin: '.5rem', marginLeft: 0 }}
      >
        Use this template
      </Button>
    );
  }

  renderEmbedded(template) {
    return (
      <div className={styles.embedded}>
        <div
          style={{
            padding: '1rem',
            display: 'flex',
            background: 'white',
            alignItems: 'center',
            borderBottom: '1px solid #eee',
            justifyContent: 'center',
          }}
        >
          <div style={{ flex: 1 }}>
            <div>{template.title}</div>
          </div>
          <Button
            primary
            target="_blank"
            style={{
              verticalAlign: 'middle',
              paddingLeft: '1rem',
              paddingRight: '1rem',
              marginRight: '1rem',
            }}
            href={`https://${this.props.template.form_slug}.paperform.co`}
          >
            View
          </Button>
          <Button
            solid
            primary
            style={{ verticalAlign: 'middle' }}
            target="_blank"
            href={`/create?template=${this.props.template.key}`}
          >
            Use this template
          </Button>
        </div>
        <Link
          style={{ display: 'block' }}
          href={`/create?template=${this.props.template.key}`}
          target="_blank"
        >
          <div
            data-paperform-id={template.form_slug}
            data-direct={1}
            width="100%"
          />
        </Link>
      </div>
    );
  }

  render() {
    const { template, related } = this.props;
    if (this.state.embedded) {
      return this.renderEmbedded(template);
    }

    return (
      <div
        style={{ opacity: this.state.loaded ? 1 : 0.03 }}
        className={styles.wrapper}
      >
        <TemplateLayout
          location={this.props.location}
          title={template.title}
          wrapperClass="bg-white"
          headerWrapperClass="!mt-[110px]"
          headerImage={formScreenshot(template.form_slug, {
            width: 2000,
            screenWidth: 961,
            fullPage: 1,
          })}
          pageContent={
            template.faq && template.faq.length > 0 ? (
              <div
                itemScope
                itemType="https://schema.org/FAQPage"
                className=" w-[700px] rounded-[16px] max-w-full mx-auto  p-1 px-6 my-12"
              >
                <h2 className="text-2xl font-400 font-sonoma">
                  {template.title} FAQs
                </h2>
                {template.faq.map((faq, i) => (
                  <div
                    key={i}
                    itemScope
                    className={`group opacity-60 transition-opacity transition-300 active:opacity-100 focus:opacity-100 hover:opacity-100 border-b-dusk/30 border-b-2 py-4 ${
                      i === template.faq.length - 1 ? ' !border-b-0' : ''
                    }`}
                    tabIndex={0}
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <h3
                      itemProp="name"
                      className=" relative text-lg m-0 leading-tight font-sonoma pr-8 cursor-pointer"
                    >
                      <MdKeyboardArrowDown className="absolute right-2 group-focus:rotate-180 my-1 " />
                      {i + 1}. {faq.question}
                    </h3>
                    <div
                      itemScope
                      itemProp="acceptedAnswer"
                      itemType="https://schema.org/Answer"
                      className="prose prose-xs transition-all opacity-0 group-active:opacity-100 group-focus:opacity-100 h-0 group-active:h-[fit-content] group-active:pt-3 group-focus:h-[fit-content] group-focus:pt-3 overflow-hidden text-xs text-black/75"
                    >
                      <span
                        itemProp="text"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : null
          }
          headerContent={
            <div>
              <div style={{ margin: '1rem 0' }}>
                {this.renderButton()}
                <span
                  data-paperform-id={template.form_slug}
                  data-popup-button="1"
                  lazy
                >
                  <Button sm>Preview</Button>
                </span>
                <Button
                  target="_blank"
                  sm
                  href={`https://${template.form_slug}.paperform.co`}
                >
                  Open in new tab
                </Button>
              </div>
              <div
                className="text-xs Template__description-content"
                dangerouslySetInnerHTML={{
                  __html:
                    replaceButtonBelowText(template.description_html) ||
                    '<p>Paperform is a web platform that enables anyone to create beautiful online forms, payment or product pages, quickly and intuitively, without any technical knowledge.</p>',
                }}
              />{' '}
            </div>
          }
          noSearch
          meta={
            <Helmet
              titleTemplate="%s | Paperform"
              title={template.meta_title || `${`${template.title} Template`}`}
            >
              {template.meta_description || template.description ? (
                <meta
                  name="description"
                  content={template.meta_description || template.description}
                />
              ) : null}
              <meta property="og:site_name" content="@PaperformCo" />
              <meta property="og:type" content="website" />
              <meta property="twitter:site" content="@PaperformCo" />
              <meta name="twitter:card" content="summary_large_image" />

              {template.cover_image_url
                ? [
                    <meta
                      key="img"
                      name="og:image"
                      content={template.cover_image_url}
                    />,
                    <meta
                      key="img2"
                      name="twitter:image"
                      content={template.cover_image_url}
                    />,
                  ]
                : null}
            </Helmet>
          }
        >
          <div style={{ width: '100%', marginTop: '.5rem' }}>
            <h3>Similar Templates</h3>
            <TemplateGrid>
              {related.map((node) => (
                <TemplateCard node={node} key={node.id} />
              ))}
            </TemplateGrid>
          </div>
        </TemplateLayout>
      </div>
    );
  }
}

export default function (props) {
  const { data } = props;
  const template = data.allPaperformTemplate.edges[0];
  const related = data.relatedTemplates.edges.map(({ node }) => node);
  return <Template {...props} related={related} template={template.node} />;
}

export const query = graphql`
  query TemplateQuery($key: String, $related_template_ids: [Int]) {
    allPaperformTemplate(filter: { key: { eq: $key } }) {
      edges {
        node {
          id
          title
          description
          description_html
          meta_description
          meta_title
          cover_image_url
          form_slug
          faq {
            question
            answer
          }
          key
          groups {
            id
            slug
            type
            title
          }
        }
      }
    }

    relatedTemplates: allPaperformTemplate(
      limit: 6
      filter: {
        key: { ne: $key }
        template_id: { in: $related_template_ids, gt: 0 }
      }
    ) {
      edges {
        node {
          id
          title
          description
          form_cover_image_url
          template_id
          form_slug
          key
        }
      }
    }
  }
`;
